// @ts-nocheck
/**
 * freehandCanvasMethods.ts
 * フリーハンドキャンバス関連メソッド
 * App.vue から切り出し
 */

import maplibregl from 'maplibre-gl'
import { geojsonCreate } from '@/js/pyramid'
import { getMdiIconPath, findStampById } from '@/js/stamps'
import { getSourceData } from '@/js/downLoad'
import { escapeHtml } from '@/js/utils/sanitize'

type VM = any

// 強化版フリーハンドキャンバスモード
export function toggleFreehandCanvas(vm: VM) {
  if (!vm.$store.state.isEditable && !vm.$store.state.isMine) {
    alert('編集不可です!')
    return
  }
  // 他のドローモードを全てOFF
  vm.$store.state.isDrawPoint = false
  vm.$store.state.isDrawCircle = false
  vm.$store.state.isDrawLine = false
  vm.$store.state.isDrawPolygon = false
  vm.$store.state.isDrawFree = false
  vm.$store.state.isDrawLasso = false
  vm.$store.state.editEnabled = false

  // キャンバスをアクティブ化（map01のみ）
  const refName = 'freehandCanvas_map01'
  const canvas = Array.isArray(vm.$refs[refName])
    ? vm.$refs[refName][0]
    : vm.$refs[refName]
  if (canvas) {
    canvas.activate()
  }
  document.querySelector('#draw-indicato-text').innerHTML = 'CANVAS'
}

export function onFreehandConfirm(vm: VM, data) {
  if (!data.paths || data.paths.length === 0) return

  // 常にmap01のソースに追加（全マップで同期されるため）
  const map = vm.$store.state.map01
  if (!map) {
    console.error('Map not found: map01')
    return
  }

  // 既存スケッチの最大番号を取得（欠番を作るため）
  let maxSketchNumber = 0
  const source = map.getSource('click-circle-source')
  if (source) {
    const geojson = getSourceData(source)
    if (geojson && geojson.features) {
      for (const f of geojson.features) {
        if (f.properties?.sketch && typeof f.properties.sketchNumber === 'number') {
          maxSketchNumber = Math.max(maxSketchNumber, f.properties.sketchNumber)
        }
      }
    }
  }

  data.paths.forEach(path => {
    if (path.points && path.points.length >= 2) {
      // 一意のIDを生成
      const id = String(Math.floor(10000 + Math.random() * 90000))

      // 色はそのまま使用（hex値でOK）
      const drawColor = path.color || '#000000'

      // スケッチ番号をインクリメント
      maxSketchNumber++

      // プロパティを設定（既存のフリーハンド機能に合わせる）
      const properties = {
        id, // IDを必ず含める
        color: drawColor,
        'line-width': path.width || 3,
        'line-opacity': 1,
        'arrow-type': 'none', // 矢印なし
        keiko: 0, // 蛍光なし
        label: '', // スケッチは地図上にラベルを表示しない
        'keiko-color': 'rgba(28,28,28,0.5)',
        offsetValue: [0.6, 0],
        textAnchor: 'left',
        textJustify: 'left',
        sketch: true, // スケッチ経由で作成されたことを示すマーカー
        sketchNumber: maxSketchNumber, // スケッチの連番（欠番対応）
      }
      // geojsonCreateを使用してドローソースに追加
      geojsonCreate(map, 'FreeHand', path.points, properties)
    }
  })
}

export function onFreehandClose() {
  document.querySelector('#draw-indicato-text').innerHTML = ''
}

// GeoJSONからスタンプマーカーを復元
export function restoreStampsFromGeojson(vm: VM, mapName = 'map01') {
  const map = vm.$store.state[mapName]
  if (!map) {
    return
  }

  const source = map.getSource('click-circle-source')
  if (!source) {
    return
  }

  const geojson = vm.getSourceData(source)
  if (!geojson || !geojson.features) {
    return
  }

  // スタンプフィーチャーを抽出
  const stampFeatures = geojson.features.filter(f => f.properties?.stamp)
  if (stampFeatures.length === 0) {
    return
  }

  // 既存のスタンプマーカーを追跡するためのリストを初期化
  if (!vm._restoredStampMarkers) {
    vm._restoredStampMarkers = []
  }

  // 既に復元済みのIDをチェック（_restoredStampMarkersとstore.state.stampMarkersの両方）
  const existingIds = new Set(vm._restoredStampMarkers.map(m => m._stampFeatureId))
  // DrawFloatingWindow.handleIconifyで作成されたマーカーも考慮
  if (vm.$store.state.stampMarkers) {
    Object.keys(vm.$store.state.stampMarkers).forEach(id => existingIds.add(id))
  }

  for (const feature of stampFeatures) {
    const props = feature.properties
    const coords = feature.geometry.coordinates

    // 既に復元済みならスキップ
    if (existingIds.has(props.id)) continue

    // スタンプマーカーを作成
    const el = document.createElement('div')
    el.className = 'stamp-marker'

    if (props.stampText) {
      el.innerHTML = `
        <div class="stamp-number-marker" style="color: ${escapeHtml(props.stampColor)}; border-color: ${escapeHtml(props.stampColor)};">
          ${escapeHtml(props.stampText)}
        </div>
      `
    } else {
      // 動的にSVGパスを解決（stampId優先、なければstampPathにフォールバック）
      let svgPath = props.stampPath
      if (props.stampId) {
        // MDIアイコンとして解決を試みる
        const mdiPath = getMdiIconPath(props.stampId)
        if (mdiPath) {
          svgPath = mdiPath
        } else {
          // 既存のスタンプ定義から解決を試みる
          const stamp = findStampById(props.stampId)
          if (stamp?.path) {
            svgPath = stamp.path
          } else if (stamp?.mdiIcon) {
            svgPath = getMdiIconPath(stamp.mdiIcon)
          }
        }
      }
      el.innerHTML = `
        <svg viewBox="0 0 24 24" width="32" height="32">
          <path fill="${props.stampColor}" d="${svgPath}" />
        </svg>
      `
    }
    el.style.cursor = 'pointer'
    el.title = props.stampLabel

    const marker = new maplibregl.Marker({
      element: el,
      anchor: 'center',
      draggable: false  // 直接ドラッグ無効（移動は編集モードで行う）
    })
      .setLngLat([coords[0], coords[1]])
      .addTo(map)

    marker._stampFeatureId = props.id
    marker._stampData = {
      id: props.stampId,
      label: props.stampLabel,
      color: props.stampColor,
      text: props.stampText,
      path: props.stampPath,
    }

    // クリックでポイントFWを開く（通常の点と同じ動作）
    el.addEventListener('click', (e) => {
      e.stopPropagation()
      // 最新のフィーチャーを取得
      const currentSource = map.getSource('click-circle-source')
      if (!currentSource) return
      const currentGeojson = vm.getSourceData(currentSource)
      if (!currentGeojson?.features) return
      const clickedFeature = currentGeojson.features.find(f => f.properties?.id === props.id)
      if (clickedFeature) {
        // 強調レイヤーに表示
        import('@/js/draw').then(({ highlightSelectedFeature }) => {
          highlightSelectedFeature(map, props.id)
        })
        // FWを開く
        vm.$store.commit('setCurrentDrawFeature', clickedFeature)
        vm.$store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
        // DrawDrawerも開く
        // setCurrentDrawFeatureがshowDrawDrawer=falseにリセットするため、nextTickで開く
        if (clickedFeature.geometry?.type === 'Point' && !clickedFeature.properties?.isCircleCenter && clickedFeature.properties?.type !== 'Text') {
          vm.$store.state.drawFeature = clickedFeature
          if (!vm.$store.state.isSmall500 || !vm.$store.state.isDraw) {
            Promise.resolve().then(() => {
              vm.$store.commit('setDrawDrawer', true)
            })
          }
        }
      }
    })

    vm._restoredStampMarkers.push(marker)

    // store.state.stampMarkersにも登録（DrawFloatingWindowでアイコン変更時に削除できるように）
    if (!vm.$store.state.stampMarkers) {
      vm.$store.state.stampMarkers = {}
    }
    vm.$store.state.stampMarkers[props.id] = marker
  }
}

// スケッチモードを強制終了（他のツールに切り替える時に使用）
export function closeFreehandCanvas(vm: VM) {
  // すべてのマップのFreehandCanvasを閉じる
  const mapNames = ['map01', 'map02', 'map03', 'map04']
  for (const mapName of mapNames) {
    const refName = 'freehandCanvas_' + mapName
    const canvas = Array.isArray(vm.$refs[refName])
      ? vm.$refs[refName][0]
      : vm.$refs[refName]
    if (canvas && (canvas.isActive || canvas.isMoveMode)) {
      canvas.close()
    }
  }
}
